/**
 * App-shell bridge for the Android Auto sessions surface (mounted once in
 * `NativeBootstrap`, native apps only). Two responsibilities:
 *
 * 1. Voice-note upload queue — init the durable uploader as soon as a user is
 *    signed in, so phone-recorded notes queued offline retry at app launch
 *    even if the Sessions tab is never opened that day.
 * 2. Provider gating of the car sessions snapshot (tap-to-navigate surface) —
 *    non-providers push an EMPTY snapshot (hides the car entry, fail closed);
 *    providers get one boot-time snapshot push via the sessions repository so
 *    the car list is fresh before the Sessions tab mounts (`use-sessions`
 *    pushes again on every load/refresh).
 *
 * Sessions modules are imported DYNAMICALLY: NativeBootstrap sits in the web
 * entry chunk, and a static import would drag the whole sessions feature
 * (repositories, upload queue) into it for every user. Mirrors the
 * auth-service sign-out cleanup pattern.
 */

import { useEffect } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { useIsProviderUser } from '../../../hooks/use-is-provider'
import { isNativeApp } from '../../../utils/platform'

/** One boot-time provider snapshot push per app launch. */
let bootSnapshotPushed = false

export function useCarSessionsBridge(): void {
  const { user } = useAuth()
  const provider = useIsProviderUser()
  const userId = user?.userId ?? null

  useEffect(() => {
    if (!isNativeApp() || !userId) return
    void import('../data/voice-note-uploader')
      .then((uploader) => uploader.initVoiceNoteUploader())
      .catch(() => {
        /* chunk load failure — retried on next mount */
      })
  }, [userId])

  const roleKnown = Boolean(user?.roleType)

  useEffect(() => {
    if (!isNativeApp() || !userId) return
    if (!provider) {
      // Fail closed for CONFIRMED members/independents: wipe the car snapshot.
      // But do NOT wipe while the role is merely unknown (auth loaded without
      // a roleType because fetchMe/profile was unreachable): `provider` is
      // transiently false then, and wiping here erased a provider's sessions
      // until the next successful boot push — one leg of the 2026-10-06
      // "lost my session button" field bug. Sign-out wipes unconditionally
      // via auth-service cleanup, so an unknown role keeps the SAME user's
      // last good snapshot only.
      if (roleKnown) {
        void import('../utils/car-sessions-sync')
          .then((sync) => sync.clearCarSessionsSnapshot())
          .catch(() => {})
      }
      return
    }
    if (bootSnapshotPushed) return
    bootSnapshotPushed = true
    void (async () => {
      try {
        const [{ getSessionsRepository }, { syncCarSessionsSnapshot }] = await Promise.all([
          import('../data/sessions-repository-provider'),
          import('../utils/car-sessions-sync'),
        ])
        const sessions = await getSessionsRepository().listMySessions()
        await syncCarSessionsSnapshot(sessions)
      } catch {
        // Keep the last good native snapshot; retry on next app launch or
        // whenever the Sessions tab loads.
        bootSnapshotPushed = false
      }
    })()
  }, [provider, roleKnown, userId])
}

/** Test-only: reset the boot-push latch. */
export function __resetCarSessionsBridgeForTest(): void {
  bootSnapshotPushed = false
}
